import { useEffect, useState } from 'react'

import { sectionList } from '../content.js'
import { UNKNOWN_NAME } from '../directory.js'
import { joinableId } from './music.js'
import { HOME_PATH, PILL_PATHS, PRESENCE_PATHS, isPresencePath, presencePathOf } from './presencePaths.js'

// Presence — who has the portal open right now. P6-242b, ADR-0010.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE DEFAULT IS ABSENT, AND ABSENT RENDERS NOTHING
// ═══════════════════════════════════════════════════════════════════════════
//
// The design's first rule and ADR-0010 D4's: no discs, no nav dots, no strip
// button, no placeholder, no reserved space — until `GET /api/presence` has
// ANSWERED. `UNAVAILABLE` is what the hook starts as, what a failed poll turns it
// back into, and what a signed-out shell stays at without asking anything. So the
// shell can merge before or after the Function that answers it: with no endpoint
// the header is byte-for-byte the header without presence.
//
// THE WIRE SHAPE, as P6-242a pinned it (`site/functions/api/presence.js`):
//
//   200 { state: "ok",     present: [{ userId, displayName, avatarUrl, path }] }
//   200 { state: "absent" }            no `present` key; any runtime failure
//   401                                signed out
//
// Absence is `state: "absent"`, any non-2xx, and anything unparseable — all
// three are `UNAVAILABLE` and render nothing. An EMPTY crew is NOT absence: it is
// a live `{ state: "ok", present: [] }`, and it renders the design's "just you"
// (see `peopleFor`) rather than nothing, because the one fact the page does have
// is that the viewer is looking at it.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 WHAT IS SENT, AND WHY IT IS NOT A URL
// ═══════════════════════════════════════════════════════════════════════════
//
// ADR-0010 D1: `POST /api/presence` with `{ "path": "<section id>" }`, and the
// Function accepts only the places in `presencePaths.js` and refuses anything
// else, so a client cannot inject a label into other members' chrome. This is
// never `location.pathname`: a member's page (a trip's id, a watch title's id) is
// not what leaves the browser.
//
// ✅ THE SET IS `home`, THE PORTAL'S PILLS, AND THE NAV'S SECTIONS — SO A MEMBER
// ANYWHERE ON THE SIGNED-IN SITE IS PRESENT. This file used to say the opposite:
// ADR-0010's letter (`home` plus the pill ids) missed the top nav, so a heartbeat
// from /docs, /commands or /releases was refused and those three pills could never
// light, and the first build sent nothing from them rather than lie. The manager
// ruled the ADR's INTENT governs (a member anywhere is present; a pill lights when
// somebody is on it) and widened the Function to match (`site/functions/api/
// presence.js`). The definition lives in ONE module, `presencePaths.js`, imported
// by the Function (write and read) and by `presencePathFor` below, and the header's
// own nav is checked against it in `presence.test.jsx` — a pill added without a
// path, or a path without a pill, fails there.

export const PRESENCE_ENDPOINT = '/api/presence'

/** ADR-0010 D1: *"Cadence and TTL are code constants: thirty seconds and
 * ninety seconds."* The TTL is the service's. The cadence is this file's, and
 * it is a third of the TTL so two lost beats in a row still leave a member in. */
export const HEARTBEAT_MS = 30_000

// The portal home's path and every path a heartbeat may name are defined once, in
// `presencePaths.js`, and re-exported here under the names this module's callers
// already use.
export { HOME_PATH }
export const ALLOWED_PATHS = PRESENCE_PATHS

/** What the hook is before it has been answered, and after anything goes wrong. */
export const UNAVAILABLE = Object.freeze({ available: false, present: Object.freeze([]) })

const SECTIONS_BY_ID = new Map(sectionList.map((section) => [section.id, section]))

/** The top-level section a registry section hangs under (itself, if it has no
 * parent). A guard against a cycle, because a registry is data and data is
 * edited by hand. */
function topLevelOf(section) {
  let current = section
  const seen = new Set()
  while (current?.parent && !seen.has(current.id)) {
    seen.add(current.id)
    current = SECTIONS_BY_ID.get(current.parent) ?? null
  }
  return current ?? null
}

/** The registry's section for the portal's front page — keyed on its ROUTE, the
 * way `presencePathOf` is, and not on the id the registry happens to give it. */
const HOME_SECTION = sectionList.find((section) => section.path === '/') ?? null

/**
 * The `path` to send for a location, or `null` when there is nothing it may say.
 *
 * Longest registry path that is the route or a SEGMENT-boundary prefix of it —
 * `/docs/watch` is `docs`, `/trips/<id>/dates` is `trips`, `/watch/<id>` hangs
 * under the portal home — and the match is case-insensitive, because the router
 * matches that way (gotcha 21 again: a guard and what it guards must agree about
 * what a path is). The answer is the TOP-LEVEL section (`/` is `home`), and it is
 * sent only if the Function would accept it — which every top-level section is,
 * by construction. `null` is a page that is not one of ours (a 404 claims no
 * place and sends nothing).
 */
export function presencePathFor(pathname) {
  if (typeof pathname !== 'string' || !pathname.startsWith('/')) return null
  const here = pathname.toLowerCase().replace(/\/+$/, '') || '/'
  let best = null
  for (const section of sectionList) {
    const path = section.path
    const hit = path === '/' ? here === '/' : here === path || here.startsWith(`${path}/`)
    if (hit && (best === null || path.length > best.path.length)) best = section
  }
  const top = best === null ? null : topLevelOf(best)
  if (top === null) return null
  const path = presencePathOf(top)
  return isPresencePath(path) ? path : null
}

const PILL_IDS = new Set(PILL_PATHS)

/**
 * The nav section a published `path` belongs to, or `null` when it names none.
 *
 * Takes what `presencePathFor` sends, and also any registry id (a section's own
 * id resolves to the top-level section it hangs under), so a member standing
 * on a child page still lands on the pill that contains it. A portal PILL id that
 * is not a registry section (`movie`, `music`, `events`, `hoops`) is a place on the
 * portal home, so it lands on the Home pill — which is what makes "every path the
 * Function accepts lights a pill" true, and `presence.test.jsx` asserts it.
 */
export function placeOf(path) {
  if (typeof path !== 'string') return null
  if (path === HOME_PATH) return topLevelOf(HOME_SECTION)
  const section = SECTIONS_BY_ID.get(path)
  if (section) return topLevelOf(section)
  return PILL_IDS.has(path) ? topLevelOf(HOME_SECTION) : null
}

/** The right-hand label of a list row — `HOME`, `TRIPS` — or `null` when the
 * path names no place we know. Never the raw string: it came over the wire. */
export function placeLabel(path) {
  const place = placeOf(path)
  return place === null ? null : String(place.short ?? place.title).toUpperCase()
}

/** The same guard `directory.js` applies to the same field, for the same
 * reason: this becomes an `<img src>`. */
function avatarUrl(raw) {
  const value = typeof raw === 'string' ? raw.trim() : ''
  return value.startsWith('https://') ? value : null
}

/**
 * The Function's answer as people, or `null` when it is not a usable answer.
 *
 * `null` for: no body, a `state` that is not `ok` (`absent` is the Function's own
 * word for "I could not say"), and a `present` that is not a list. An EMPTY list
 * is an answer — a live crew of nobody — and comes back as `[]`, which is not
 * `null`: the two are different facts and the page treats them differently.
 *
 * A person without a joinable id is dropped — the id is what colours the
 * fallback disc and tells the viewer which one is them, and `joinableId` is the
 * site's one answer to *"is this an id"* (P6-177). A person with no name is KEPT
 * and called `UNKNOWN_NAME`: they are still here.
 */
export function readPresence(body) {
  if (body === null || typeof body !== 'object') return null
  if (body.state !== 'ok' || !Array.isArray(body.present)) return null
  const people = []
  const seen = new Set()
  for (const entry of body.present) {
    const userId = joinableId(entry?.userId)
    if (userId === null || seen.has(userId)) continue
    seen.add(userId)
    const name = typeof entry.displayName === 'string' ? entry.displayName.trim() : ''
    people.push({
      userId,
      displayName: name === '' ? UNKNOWN_NAME : name,
      avatarUrl: avatarUrl(entry.avatarUrl),
      path: typeof entry.path === 'string' ? entry.path : null,
    })
  }
  return people
}

/**
 * The viewer first, everyone else by name.
 *
 * ⚠️ SORTED, NOT LEFT IN THE SERVER'S ORDER. The service keeps a map and a map's
 * order is an implementation detail; a row of discs that reshuffled on every
 * poll would be motion on a surface whose first rule is that nothing moves.
 */
export function orderPeople(people, youId) {
  const rank = (person) => (person.userId === youId ? 0 : 1)
  return [...people].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      a.displayName.localeCompare(b.displayName, 'en', { sensitivity: 'base' }) ||
      (a.userId < b.userId ? -1 : 1),
  )
}

/** What the viewer is called when nothing else says. */
export const YOU_NAME = 'You'

/**
 * The crew as the shell draws it: ordered, with the viewer first — and with the
 * viewer THERE when the crew is empty.
 *
 * 🔴 AN EMPTY CREW IS "JUST YOU", NOT ABSENCE. The Function answers
 * `{ state: "ok", present: [] }` when it is healthy and nobody's heartbeat has
 * landed — including the viewer's own, on the first beat or on a page it takes no
 * heartbeat from. The member is, in fact, looking at the portal, so the design's
 * *only you* state is true: one disc, `JUST YOU`. The entry is built from the
 * session's own id (no name is invented: `You` is a pronoun, and the list's
 * right-hand label says where) and ONLY for an empty crew. A crew that is not
 * empty and does not include the viewer is drawn as the Function said — it is the
 * one party that can see everybody.
 */
export function peopleFor({ present, youId, pathname }) {
  if (present.length > 0) return orderPeople(present, youId)
  const id = joinableId(youId)
  if (id === null) return []
  return [{ userId: id, displayName: YOU_NAME, avatarUrl: null, path: presencePathFor(pathname) }]
}

/** Who is on which nav section, the viewer left out: a dot that is always lit
 * on the page you are standing on says nothing. Keyed by section id. */
export function peopleBySection(people, youId) {
  const bySection = new Map()
  for (const person of people) {
    if (person.userId === youId) continue
    const place = placeOf(person.path)
    if (place === null) continue
    const there = bySection.get(place.id) ?? []
    there.push(person)
    bySection.set(place.id, there)
  }
  return bySection
}

/** "Theo", "Theo and Ro", "Theo, Ro and Sam" — the sentence for a title. */
export function joinNames(people) {
  const names = people.map((person) => person.displayName)
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** One poll of `GET /api/presence`, as the hook's state. Never throws. */
async function read(fetchImpl) {
  try {
    const response = await fetchImpl(PRESENCE_ENDPOINT, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) return UNAVAILABLE
    const people = readPresence(await response.json())
    return people === null ? UNAVAILABLE : { available: true, present: people }
  } catch {
    return UNAVAILABLE
  }
}

/** One heartbeat. Silent on every failure, by ADR-0010 D4: a missed beat is
 * retried at the next one, and the TTL is the truth. */
async function beat(fetchImpl, path) {
  try {
    await fetchImpl(PRESENCE_ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ path }),
    })
  } catch {
    // Silent on purpose. There is nobody to tell and nothing to retry sooner.
  }
}

function same(a, b) {
  if (a === b) return true
  if (a.available !== b.available || a.present.length !== b.present.length) return false
  return a.present.every((person, index) => {
    const other = b.present[index]
    return (
      person.userId === other.userId &&
      person.displayName === other.displayName &&
      person.avatarUrl === other.avatarUrl &&
      person.path === other.path
    )
  })
}

/**
 * Presence for the signed-in shell.
 *
 * While the tab is VISIBLE: a heartbeat and a read at once, then every
 * `HEARTBEAT_MS`. When the tab is hidden both stop — the TTL clears the member —
 * and the moment it is visible again they start with an immediate beat. On
 * `pagehide` a `DELETE` with `keepalive` goes out as a courtesy (ADR-0010 D1:
 * *"the TTL is the truth"*).
 *
 * `enabled` is the session: a signed-out or still-loading shell asks nothing
 * and stays `UNAVAILABLE`. `pathname` re-keys the effect, so moving between
 * sections sends a beat at once and the dots follow the member rather than
 * trailing by up to half a minute.
 *
 * 🔴 THE READ FOLLOWS THE BEAT, IN ORDER. A member's own heartbeat has to have
 * landed before they ask who is here, or the first strip they see is one that
 * does not include them.
 */
export function usePresence({ enabled, pathname }) {
  const [state, setState] = useState(UNAVAILABLE)
  const path = enabled ? presencePathFor(pathname) : null

  useEffect(() => {
    if (!enabled) {
      setState(UNAVAILABLE)
      return undefined
    }
    const fetchImpl = globalThis.fetch
    if (typeof fetchImpl !== 'function') return undefined

    let cancelled = false
    let timer = null

    const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden'

    async function tick() {
      if (path !== null) await beat(fetchImpl, path)
      const answer = await read(fetchImpl)
      if (!cancelled) setState((was) => (same(was, answer) ? was : answer))
    }
    function stop() {
      if (timer !== null) clearInterval(timer)
      timer = null
    }
    function start() {
      stop()
      tick()
      timer = setInterval(tick, HEARTBEAT_MS)
    }
    function onVisibility() {
      if (visible()) start()
      else stop()
    }
    function onPageHide() {
      try {
        fetchImpl(PRESENCE_ENDPOINT, {
          method: 'DELETE',
          credentials: 'same-origin',
          keepalive: true,
        }).catch(() => {})
      } catch {
        // A courtesy; the TTL clears the member either way.
      }
    }

    if (visible()) start()
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      cancelled = true
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [enabled, path])

  return enabled ? state : UNAVAILABLE
}
